import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useMarketingEmbedded } from '../hooks/useMarketingEmbedded';
import MarketingPageHeader from '../components/marketing/MarketingPageHeader';
import {
  loadLeadsCalculatorDataForUser,
  saveLeadsCalculatorDataForUser,
} from '../services/leadsCalculatorService';
import PortalBackground from '../components/PortalBackground';

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Percentages are whole numbers (25 = 25%).
const DEFAULT_INPUTS = {
  revenueGoal: 1000000,
  avgJobSize: 50000,
  closeRatio: 25,
  qualifiedPct: 60,
  costPerQualifiedLead: 400,
};

// Saved shape lives on users/{uid}.leadsCalculator.formData (shared with the
// Marketing ▸ Analytics tab): revenue / averageJobSize / closeRatio /
// qualifiedLeadPercent / costPerQualifiedLead. Positive saved values win.
const inputsFromSaved = (saved) => {
  const f = saved?.formData || {};
  const pick = (v, fallback) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
  return {
    revenueGoal: pick(f.revenue, DEFAULT_INPUTS.revenueGoal),
    avgJobSize: pick(f.averageJobSize, DEFAULT_INPUTS.avgJobSize),
    closeRatio: pick(f.closeRatio, DEFAULT_INPUTS.closeRatio),
    qualifiedPct: pick(f.qualifiedLeadPercent, DEFAULT_INPUTS.qualifiedPct),
    costPerQualifiedLead: pick(f.costPerQualifiedLead, DEFAULT_INPUTS.costPerQualifiedLead),
  };
};

const formDataFromInputs = (inputs) => ({
  revenue: num(inputs.revenueGoal),
  averageJobSize: num(inputs.avgJobSize),
  closeRatio: num(inputs.closeRatio),
  qualifiedLeadPercent: num(inputs.qualifiedPct),
  costPerQualifiedLead: num(inputs.costPerQualifiedLead),
});

const resultsFromCalc = (calc) => {
  const safe = (v) => (Number.isFinite(v) ? Math.ceil(v) : 0);
  return {
    contractsNeeded: safe(calc.contracts),
    proposalsNeeded: safe(calc.proposals),
    leadsNeededAnnually: safe(calc.raw),
    leadsNeededQuarterly: safe(calc.raw / 4),
    leadsNeededMonthly: safe(calc.raw / 12),
    leadsNeededWeekly: safe(calc.raw / 52),
  };
};

const money = (v, digits = 0) =>
  Number.isFinite(v)
    ? v.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : '–';

const n1 = (v) =>
  Number.isFinite(v)
    ? v.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : '–';

const pct = (v) => (Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : '–');

// Labeled number input with an optional $ prefix or % suffix (same style as the Margin Calculator).
const CalcInput = ({ darkMode, label, hint, prefix, suffix, ...inputProps }) => (
  <div className={`rounded-xl border p-4 shadow-sm bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
    <label className={`block text-sm font-semibold text-center ${hint ? 'mb-0.5' : 'mb-2'} ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
      {label}
    </label>
    {hint && (
      <div className={`text-xs text-center mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{hint}</div>
    )}
    <div className="relative rounded-md shadow-sm">
      {prefix && (
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{prefix}</span>
        </div>
      )}
      <input
        type="number"
        className={`block w-full rounded-md border py-2 ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'} focus:border-red-500 focus:ring-red-500 text-lg text-center ${
          darkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
        }`}
        {...inputProps}
      />
      {suffix && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{suffix}</span>
        </div>
      )}
    </div>
  </div>
);

// Calculated result tile; `highlight` marks a key result.
const ResultCard = ({ darkMode, label, hint, value, highlight = false }) => (
  <div
    className={`rounded-xl border p-4 shadow-sm flex flex-col justify-center items-center text-center ${
      highlight
        ? darkMode
          ? 'bg-red-900/20 border-red-800'
          : 'bg-red-50 border-red-200'
        : darkMode
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
    }`}
  >
    <div className={`text-xs ${hint ? '' : 'mb-1'} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{label}</div>
    {hint && (
      <div className={`text-[11px] mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{hint}</div>
    )}
    <div className={`text-xl font-bold ${highlight ? (darkMode ? 'text-red-300' : 'text-red-600') : ''}`}>{value}</div>
  </div>
);

const SectionHeading = ({ darkMode, emoji, title }) => (
  <div className="flex items-center justify-center gap-2 text-center mb-4">
    {emoji && <span className="text-xl leading-none" aria-hidden="true">{emoji}</span>}
    <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-700'}`}>{title}</h2>
  </div>
);

/**
 * Lead Calculator — works backwards from an annual revenue goal to the leads
 * and marketing budget required to hit it.
 *
 * overrideUserId lets an internal viewer (CRM Portal View) load/save a specific
 * client's inputs instead of their own.
 */
const LeadsCalculator = ({ embedded: embeddedProp, overrideUserId = null }) => {
  const embedded = useMarketingEmbedded(embeddedProp);
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const effectiveUid = overrideUserId || currentUser?.uid;

  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [loading, setLoading] = useState(true);

  // Autosave only after the initial load, and skip the save caused by loading data into state.
  const didLoadRef = useRef(false);
  const skipNextSaveRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!effectiveUid) return;
      setLoading(true);
      let saved = null;
      try {
        saved = await loadLeadsCalculatorDataForUser(effectiveUid);
      } catch (err) {
        console.error('Error loading leads calculator data:', err);
      }
      if (cancelled) return;
      skipNextSaveRef.current = true;
      didLoadRef.current = true;
      setInputs(inputsFromSaved(saved));
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [effectiveUid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: value === '' ? '' : num(value) }));
  };

  const calc = useMemo(() => {
    const rev = num(inputs.revenueGoal);
    const job = num(inputs.avgJobSize);
    const close = num(inputs.closeRatio) / 100;
    const qual = num(inputs.qualifiedPct) / 100;
    const cpql = num(inputs.costPerQualifiedLead);

    const contracts = rev / job;
    const proposals = contracts / close;
    const raw = proposals / qual;
    const annualCost = proposals * cpql;

    return {
      contracts,
      proposals,
      raw,
      annualCost,
      costPerRawLead: cpql * qual,
      cac: cpql / close,
      costPct: annualCost / rev,
    };
  }, [inputs]);

  // Debounced autosave in the shared users/{uid}.leadsCalculator shape.
  useEffect(() => {
    if (!didLoadRef.current || !effectiveUid) return;
    if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return; }
    const t = setTimeout(() => {
      saveLeadsCalculatorDataForUser(effectiveUid, formDataFromInputs(inputs), resultsFromCalc(calc))
        .catch((err) => console.error('Error auto-saving leads calculator data:', err));
    }, 1000);
    return () => clearTimeout(t);
  }, [inputs, calc, effectiveUid]);

  const periodRows = [
    { label: 'Annual', divisor: 1 },
    { label: 'Monthly', divisor: 12 },
    { label: 'Weekly', divisor: 52 },
  ];

  return (
    <div className={`text-gray-900 dark:text-white ${embedded ? '' : 'relative p-6 max-w-6xl mx-auto min-h-screen'}`}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {!embedded && <MarketingPageHeader title="Lead Calculator" />}

      {loading && (
        <p className={`text-xs text-center italic mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Loading your saved inputs…
        </p>
      )}

      {/* Top row: inputs column on the left, funnel + period table on the right */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-6 mb-8">
        {/* Inputs */}
        <div>
          <SectionHeading darkMode={darkMode} emoji="✏️" title="Inputs" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            <CalcInput darkMode={darkMode} label="Annual Revenue Goal" name="revenueGoal" value={inputs.revenueGoal} onChange={handleChange} prefix="$" min="0" step="10000" placeholder="1000000" />
            <CalcInput darkMode={darkMode} label="Average Job Size" name="avgJobSize" value={inputs.avgJobSize} onChange={handleChange} prefix="$" min="0" step="1000" placeholder="50000" />
            <CalcInput darkMode={darkMode} label="Close Ratio" hint="Proposals that become contracts" name="closeRatio" value={inputs.closeRatio} onChange={handleChange} suffix="%" min="0" max="100" step="1" placeholder="25" />
            <CalcInput darkMode={darkMode} label="Qualified Lead %" hint="Raw leads that get a proposal" name="qualifiedPct" value={inputs.qualifiedPct} onChange={handleChange} suffix="%" min="0" max="100" step="1" placeholder="60" />
            <CalcInput darkMode={darkMode} label="Cost per Qualified Lead" hint="Marketing spend per qualified lead" name="costPerQualifiedLead" value={inputs.costPerQualifiedLead} onChange={handleChange} prefix="$" min="0" step="10" placeholder="400" />
          </div>
        </div>

        {/* Funnel + leads by period */}
        <div className="flex flex-col gap-8">
          <div>
            <SectionHeading darkMode={darkMode} emoji="🔻" title="Annual Funnel" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <ResultCard darkMode={darkMode} label="Contracts Needed" hint="Revenue ÷ avg job size" value={n1(calc.contracts)} />
              <ResultCard darkMode={darkMode} label="Proposals Needed (Qualified Leads)" hint="Contracts ÷ close ratio" value={n1(calc.proposals)} />
              <ResultCard darkMode={darkMode} label="Raw Leads Needed" hint="Proposals ÷ qualified %" value={n1(calc.raw)} />
            </div>
          </div>

          <div>
            <SectionHeading darkMode={darkMode} emoji="📅" title="Leads by Period" />
            <div className={`rounded-xl border shadow-sm overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-50'}>
                    <tr>
                      {['Period', 'Qualified Leads', 'Raw Leads'].map((h, i) => (
                        <th
                          key={h}
                          scope="col"
                          className={`px-6 py-4 text-sm font-semibold uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-right'} ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'bg-gray-900 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                    {periodRows.map((row) => (
                      <tr key={row.label}>
                        <td className={`px-6 py-5 whitespace-nowrap text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{row.label}</td>
                        <td className={`px-6 py-5 whitespace-nowrap text-2xl text-right font-bold tabular-nums ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{n1(calc.proposals / row.divisor)}</td>
                        <td className={`px-6 py-5 whitespace-nowrap text-2xl text-right font-semibold tabular-nums ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{n1(calc.raw / row.divisor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Marketing cost — under Leads by Period in the right column */}
          <div>
            <SectionHeading darkMode={darkMode} emoji="💰" title="Marketing Cost" />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <ResultCard darkMode={darkMode} label="Total Annual Marketing Cost" hint="Qualified leads × cost per qualified lead" value={money(calc.annualCost)} highlight />
              <ResultCard darkMode={darkMode} label="Monthly Marketing Budget" hint="Annual marketing cost ÷ 12" value={money(calc.annualCost / 12)} />
              <ResultCard darkMode={darkMode} label="Weekly Marketing Budget" hint="Annual marketing cost ÷ 52" value={money(calc.annualCost / 52)} />
              <ResultCard darkMode={darkMode} label="Cost per Raw Lead (Implied)" hint="Cost per qualified lead × qualified %" value={money(calc.costPerRawLead, 2)} />
              <ResultCard darkMode={darkMode} label="Cost per Contract (CAC)" hint="Cost per qualified lead ÷ close ratio" value={money(calc.cac, 2)} />
              <ResultCard darkMode={darkMode} label="Marketing Cost as % of Revenue" hint="Annual marketing cost ÷ revenue goal" value={pct(calc.costPct)} highlight />
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default LeadsCalculator;
